/**
 * Scroll-reveal JS fallback for browsers WITHOUT native scroll-driven
 * animations (Safari, Firefox today).
 *
 * Progressive enhancement contract:
 *   - Chromium supports `animation-timeline: view()` → this does nothing;
 *     the pure-CSS path in scrollReveal.css drives the reveal.
 *   - prefers-reduced-motion: reduce → this does nothing; content stays
 *     fully visible.
 *   - Otherwise → add `bp-reveal-js` to <html> (which makes the fallback CSS
 *     take over) and use an IntersectionObserver to toggle `.is-revealed` on
 *     each `.bp-reveal` element as it enters/leaves the viewport. Because the
 *     hidden state is ONLY applied once this JS runs, content is fully visible
 *     if JS is disabled or fails.
 */
export function initScrollReveal() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const supportsViewTimeline =
    typeof CSS !== 'undefined' && CSS.supports && CSS.supports('animation-timeline: view()');
  if (supportsViewTimeline) return; // native CSS path handles it

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return; // leave everything visible

  const root = document.documentElement;
  root.classList.add('bp-reveal-js');

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-revealed');
        } else if (e.boundingClientRect.top > 0) {
          // Only reset when the element has left via the BOTTOM (scrolled back
          // up past it) so it can replay. Elements that scrolled off the top
          // stay revealed. This mirrors the native view() reverse behaviour.
          e.target.classList.remove('is-revealed');
        }
      });
    },
    { threshold: 0.14, rootMargin: '0px 0px -8% 0px' }
  );

  const observe = () => {
    document.querySelectorAll('.bp-reveal:not([data-reveal-observed])').forEach((el) => {
      el.setAttribute('data-reveal-observed', '1');
      io.observe(el);
    });
  };

  observe();

  // SPA route changes mount new .bp-reveal nodes - rescan, debounced via rAF.
  let scheduled = false;
  const mo = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      observe();
    });
  });
  mo.observe(document.body, { childList: true, subtree: true });
}
